"use client";

import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateUuid, isBoolean } from "@huub-web/utils";
import { AnimatePresence, motion } from "motion/react";

import type { ChildrenProp } from "../../props";
import { Snackbar, type SnackbarProps } from "../Snackbar";
import { Portal } from "../Portal";
import {
  buttonStyle,
  containerStyle,
  snackbarStyle,
  stackItemStyle,
  stackStyle,
} from "./SnackbarController.css";
import { usePubSub } from "../../hooks/usePubSub";
import {
  snackbarStackItemVariants,
  snackbarContainerVariants,
} from "./SnackbarController.motion";
import { Button } from "../Button";
import { Reveal } from "../Reveal";
import { transition } from "../../utils/transition";
import { Badge } from "../Badge";

export type SnackbarRecord = Pick<
  SnackbarProps,
  "body" | "title" | "autoHide" | "closeable" | "variant"
>;

export type SnackbarControllerProps = ChildrenProp;

type SnackbarWithID = SnackbarRecord & { id: string };

/**
 * SnackbarController is a component that provides a context for the Snackbar component.
 */
export const SnackbarController: FC<SnackbarControllerProps> = ({
  children,
}) => {
  const { t } = useTranslation();
  const pubSub = usePubSub();

  const ref = useRef<HTMLDivElement>(null);
  const [snackbars, setSnackbars] = useState<SnackbarWithID[]>([]);

  const handleClose = (id: string) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  };

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("snackbar", (data) => {
      const id = generateUuid();

      setSnackbars((prev) => [
        ...prev,
        {
          id,
          ...data,
          autoHide: data.autoHide ?? 5,
          closeable: isBoolean(data.closeable) ? data.closeable : true,
          onClose: () => handleClose(id),
        },
      ]);
    });

    return () => {
      unsubscribe();
    };
  }, [pubSub]);

  const count = Math.min(snackbars.length, 3);

  return (
    <Portal>
      <Reveal>
        {count > 1 && (
          <div className={buttonStyle}>
            <Button
              trailing={
                <Badge value={snackbars.length} offset="medium" size="xs" />
              }
              size="xxs"
              variant="opaque"
              border="default"
              onClick={() => {
                setSnackbars([]);
              }}
            >
              {t("snackbar.clearNotifications")}
            </Button>
          </div>
        )}
      </Reveal>

      <div ref={ref} className={containerStyle({ moreThanOne: count > 1 })}>
        <div
          style={{
            height: `${(count - 1) * 12}px`,
            transition: transition("height"),
          }}
        />

        <motion.div
          className={stackStyle()}
          variants={snackbarContainerVariants}
          initial="initial"
          animate="animate"
        >
          <AnimatePresence mode="popLayout">
            {snackbars.map(({ id, ...props }, index) => {
              if (index > 3) {
                return null;
              }

              return (
                <motion.div
                  key={id}
                  layout
                  className={stackItemStyle()}
                  variants={snackbarStackItemVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  custom={{ index, count }}
                  style={{
                    zIndex: snackbars.length - index,
                  }}
                >
                  <Snackbar
                    className={snackbarStyle()}
                    paused={index !== 0}
                    index={index}
                    {...props}
                    body={index !== 0 ? <div /> : props.body}
                    onClose={() => handleClose(id)}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>

          {children}
        </motion.div>
      </div>
    </Portal>
  );
};
